import { ProjectPathParams, Paths } from '../../../app/router/app.router';
import { useDeliveriesList } from '../list/hooks/use-deliveries-list.hook';
import { useMemo, useEffect, useState } from 'react';
import { RestError } from '@azure/core-http';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { makeDeliveriesListPageHeaderProps } from '../list/logic/deliveries-list-props.map';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { ListPageHeader } from '../../../ui/components/pure/page-headers/list/list-page-header.component';
import { DeliveriesTable } from '../list/components/deliveries-table.component';
import { DeliveriesListActions } from '../list/actions/deliveries-list.actions';
import { DeliveriesListActionsObserver } from '../list/deliveries-list-actions-observer';
import { Container, Button, Menu, MenuItem, Checkbox } from '@mui/material';
import { PermissionBoundary } from '../../../core/permissions/permission-boundary.component';

const AllColumnKeys = [
  'project',
  'hub',
  'type',
  'by',
  'agent',
  'deliveryTime',
  'to',
  'barcode',
  'unit',
  'lockerName',
  'lockerSize',
  'completedTime',
  'collectionCode',
  'channel',
  'status',
  'notifications'
] as const;

type ColumnKey = (typeof AllColumnKeys)[number];

const INTERNAL_COLUMNS_KEY = 'internalView.columns.__GLOBAL__';

const KeyToLabelMap: Record<ColumnKey, string> = {
  project: 'Project',
  hub: 'Hub',
  type: 'Type',
  by: 'By',
  agent: 'Agent',
  deliveryTime: 'Delivery Started Time',
  to: 'To',
  barcode: 'Barcode',
  unit: 'Unit',
  lockerName: 'Locker Name',
  lockerSize: 'Locker Size',
  completedTime: 'Completed Time',
  collectionCode: 'Collection Code',
  channel: 'Channel',
  status: 'Status',
  notifications: 'Notifications'
};

export const DeliveriesInternalPage = ({ params }: { params: ProjectPathParams<Paths.deliveries> }) => {
  const {
    project,
    deliveries,
    infiniteScroll,
    formData,
    setFormData,
    hubFilter,
    carrierFilter,
    showHubNameInTable,
    handlers,
    state,
    showReceiveDeliveryButton
  } = useDeliveriesList(params.projectID);

  const sortData = useMemo(
    () => ({ sortBy: formData.sortBy, sortOrder: formData.sortOrder }),
    [formData.sortBy, formData.sortOrder]
  );

  const defaultVisibleColumnKeys: ColumnKey[] = [
    'project',
    'hub',
    'type',
    'by',
    'agent',
    'deliveryTime',
    'to',
    'unit',
    'completedTime',
    'channel',
    'status',
    'notifications'
  ];

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<ColumnKey[]>(() => {
    try {
      let saved = JSON.parse(localStorage.getItem(INTERNAL_COLUMNS_KEY) ?? 'null');

      if (!Array.isArray(saved) || saved.length === 0) {
        const legacy = JSON.parse(localStorage.getItem(`internalView.columns.${params.projectID}`) ?? 'null');
        if (Array.isArray(legacy) && legacy.length > 0) {
          saved = legacy;
          localStorage.setItem(INTERNAL_COLUMNS_KEY, JSON.stringify(saved));
        }
      }

      return Array.isArray(saved) && saved.length > 0 ? saved : defaultVisibleColumnKeys;
    } catch {
      return defaultVisibleColumnKeys;
    }
  });

  const columns = useMemo(() => {
    return AllColumnKeys.map((key) => ({
      key,
      label: KeyToLabelMap[key],
      visible: visibleColumnKeys.includes(key)
    }));
  }, [visibleColumnKeys]);

  const memoizedVisibleColumns = useMemo(() => columns.filter((col) => col.visible), [columns]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleToggleColumn = (key: ColumnKey) => {
    setVisibleColumnKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  useEffect(() => {
    localStorage.setItem(INTERNAL_COLUMNS_KEY, JSON.stringify(visibleColumnKeys));
  }, [visibleColumnKeys]);

  const error = deliveries.error;
  if (error) {
    if ((error as RestError).statusCode === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }
    if ((error as RestError).statusCode === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }
    if ((error as RestError).statusCode === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }
    return <ErrorFullPage />;
  }

  const headerProps = makeDeliveriesListPageHeaderProps(
    params.projectID,
    deliveries.data?.pages[0]?.totalRecords || 0,
    () => handlers.setAction(DeliveriesListActions.CREATE_DELIVERY),
    formData,
    setFormData,
    hubFilter,
    carrierFilter,
    showReceiveDeliveryButton
  );

  return (
    <Container>
      <PermissionBoundary
        availableUiFeatures={['internal-groundfloor']}
        notify={{
          render() {
            return <ErrorFullPage preset="ACCESS_DENIED" />;
          }
        }}
      >
        {project.data ? <WindowTitle pageTitle="Internal View" detail={project.data.name} /> : undefined}
        <ListPageHeader {...headerProps} />

        <Button
          onClick={handleMenuClick}
          style={{
            color: 'white',
            backgroundColor: '#444'
          }}
        >
          Columns
        </Button>

        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <div style={{ maxHeight: '240px', overflowY: 'auto', paddingRight: '8px' }}>
            {columns.map((col) => (
              <MenuItem key={col.key} onClick={() => handleToggleColumn(col.key)}>
                <Checkbox
                  checked={col.visible}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleColumn(col.key);
                  }}
                />
                {col.label}
              </MenuItem>
            ))}
          </div>
        </Menu>

        <DeliveriesTable
          viewId="internal"
          params={params}
          pages={deliveries.data?.pages || []}
          infiniteScroll={infiniteScroll}
          setFormData={setFormData}
          sortData={sortData}
          isLoading={deliveries.isLoading}
          isPreviousData={deliveries.isPreviousData}
          hasError={deliveries.isError}
          showHubName={showHubNameInTable}
          columns={memoizedVisibleColumns}
        />
        <DeliveriesListActionsObserver projectID={params.projectID} handlers={handlers} state={state} />
      </PermissionBoundary>
    </Container>
  );
};
